import { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { constants } from "@qgiv/core-js";
import {
    getCategories,
    getClassifications,
    getTeams,
    getProducts,
    getIsTeamAttachedToClassification,
} from "@qgiv/p2p-registration";
import { updateRegistrationClassification } from "../redux/slices/registrationSlice";

const UpdateOutdated = ({
    classificationId = 0,
    erroredEntities = [],
    formId,
    outdated = [],
    registrations = [],
    setErroredEntities,
    setOutdated,
    setUnsetSelectedClassification,
    teamsList,
    updateCategories,
    updateClassifications,
    updateProducts,
    updateRegistration,
    updateTeams,
}) => {
    const dispatch = useDispatch();
    const { ENUMS } = constants;
    // NOTE to come back and pay attention to this file in the context of commitments;

    const {
        EntityType: { CATEGORY, CLASSIFICATION, TEAM, PURCHASE },
    } = ENUMS;

    const getApiCall = (entityType) => {
        // -------------------------------------------------------------------------
        // NOTE: Team Params
        // When making this call for teams after receiving outdated, we are currently using the same api endpoint
        // that the JoinTeamModal uses to get the list of teams to join. The teams returned will be ALL
        // of the teams a user can see on that modal, including teams with 0 spots left.
        // If the user has a selected classification and `limitTeamJoiningToClassification` is enabled
        // the list of teams will only include teams for that classification.
        // -------------------------------------------------------------------------
        let teamsParams = {};
        if (classificationId > 0) {
            teamsParams = {
                classificationId,
                searchTeams: true,
                searchKey: "",
            };
        } else {
            teamsParams = {
                allTeams: true,
            };
        }
        switch (entityType) {
            case CATEGORY:
                return getCategories(formId)
                    .then((apiResponse) => {
                        const { data = {} } = apiResponse;
                        const { response = {} } = data;
                        const { categories = [] } = response;
                        dispatch(updateCategories(categories));
                    })
                    .catch(() => Promise.reject());
            case CLASSIFICATION:
                return getClassifications(formId)
                    .then((apiResponse) => {
                        const { data = {} } = apiResponse;
                        const { response = {} } = data;
                        const { classifications = [] } = response;
                        dispatch(updateClassifications(classifications));
                        dispatch(setUnsetSelectedClassification(true));
                        dispatch(
                            updateRegistrationClassification({
                                Classification: [""],
                            }),
                        );
                    })
                    .catch(() => Promise.reject());
            case TEAM:
                return getTeams(formId, teamsParams)
                    .then((apiResponse) => {
                        const { data = {} } = apiResponse;
                        const { response = {} } = data;
                        const { teams = [] } = response;
                        dispatch(updateTeams(teams));
                    })
                    .catch(() => Promise.reject());
            case PURCHASE:
                return getProducts(formId).then((apiResponse) => {
                    const { data = {} } = apiResponse;
                    const { response = {} } = data;
                    const { products = [] } = response;
                    dispatch(updateProducts(products));
                });
            default: {
                return Promise.resolve();
            }
        }
    };

    useEffect(() => {
        if (outdated.length > 0) {
            Promise.all([
                ...outdated.map((entityType) => getApiCall(entityType)),
            ]).then(() => {
                dispatch(setOutdated([]));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outdated]);

    // Unset the pertinent values on [registrations] when a registration contains
    // the entity that is outdated (Team, Category)
    useEffect(() => {
        if (erroredEntities.length > 0) {
            // using for...of loops for performance with nested array loop
            // eslint-disable-next-line no-restricted-syntax
            for (const entityOutdated of erroredEntities) {
                const { entityType, entity } = entityOutdated;
                // eslint-disable-next-line no-restricted-syntax
                for (const [i, registration] of registrations.entries()) {
                    switch (entityType) {
                        case CLASSIFICATION:
                            {
                                // If the classification is no longer valid and the Team joining is
                                // limited to Classification then we have to unset the Team
                                const isTeamAttachedToOutdatedClassification =
                                    getIsTeamAttachedToClassification({
                                        teams: teamsList,
                                        teamId: registration.Team,
                                        classificationId: classificationId || 0,
                                    });

                                if (isTeamAttachedToOutdatedClassification) {
                                    dispatch(
                                        updateRegistration({
                                            index: i,
                                            registration: {
                                                Team: "",
                                                Needs_To_Be_Reviewed: true,
                                            },
                                        }),
                                    );
                                }
                            }
                            break;
                        case TEAM:
                            {
                                const { Team } = registration;
                                if (Team && entity === Number(Team)) {
                                    dispatch(
                                        updateRegistration({
                                            index: i,
                                            registration: {
                                                Team: "",
                                                Needs_To_Be_Reviewed: true,
                                            },
                                        }),
                                    );
                                }
                            }
                            break;
                        case CATEGORY:
                            {
                                const { Category } = registration;
                                if (
                                    Category.length > 0 &&
                                    entity === Number(Category[0])
                                ) {
                                    dispatch(
                                        updateRegistration({
                                            index: i,
                                            registration: {
                                                Category: [],
                                                Needs_To_Be_Reviewed: true,
                                            },
                                        }),
                                    );
                                }
                            }
                            break;
                        // Todo PROMO-CODES
                        default: {
                            // The BE does send back erroredEntity data when the entityType is Classification,
                            // but since there is only one classification per registration that information is not needed.
                            break;
                        }
                    }
                }
            }
            dispatch(setErroredEntities([]));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroredEntities]);

    return null;
};

UpdateOutdated.propTypes = {
    formId: PropTypes.number.isRequired,
    outdated: PropTypes.arrayOf(PropTypes.number),
    // If classification on registration exists, will be id
    // if it does not exist, will be 0, used for setting getTeams params
    classificationId: PropTypes.number,
    erroredEntities: PropTypes.arrayOf(PropTypes.shape({})),
    registrations: PropTypes.arrayOf(PropTypes.shape({})),
    updateRegistration: PropTypes.func.isRequired,
    setOutdated: PropTypes.func.isRequired,
    setErroredEntities: PropTypes.func.isRequired,
    setUnsetSelectedClassification: PropTypes.func.isRequired,
    teamsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    updateCategories: PropTypes.func.isRequired,
    updateClassifications: PropTypes.func.isRequired,
    updateProducts: PropTypes.func.isRequired,
    updateTeams: PropTypes.func.isRequired,
};

export default UpdateOutdated;
